import React from "react"

import { ChevronDown, ChevronRight, Radio } from "lucide-react"

import type { Message as MessageType } from "@/types/Transcript"

import { TimestampDisplay } from "./TimestampDisplay"

interface SystemEventMessageProps {
  message: MessageType
  index: number
  isSelected?: boolean
  onClick?: (index: number) => void
  className?: string
}

interface ParsedSystemEvent {
  eventType: string
  eventName: string | null
  details: unknown
  detailsText: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

export const isWebsocketEventMessage = (message: MessageType): boolean =>
  message.role === "websocket_event" ||
  (message.role === "system" && message.name === "websocket_event")

const parseJsonContent = (content: string): unknown => {
  try {
    return JSON.parse(content) as unknown
  } catch {
    return content
  }
}

const formatDetails = (details: unknown): string => {
  if (typeof details === "string") {
    return details
  }

  return JSON.stringify(details, null, 2)
}

// Agents may send the field as an empty string, which is not a usable label.
const asLabel = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null
  }

  const trimmed = value.trim()
  return trimmed === "" ? null : trimmed
}

const parseSystemEvent = (message: MessageType): ParsedSystemEvent => {
  const content = parseJsonContent(message.content)

  if (!isRecord(content)) {
    return {
      eventType: "websocket_event",
      eventName: asLabel(message.name),
      details: content,
      detailsText: formatDetails(content),
    }
  }

  return {
    eventType: asLabel(content.event_type) ?? "websocket_event",
    eventName: asLabel(content.event_name),
    details: content,
    detailsText: formatDetails(content),
  }
}

export const SystemEventMessage: React.FC<SystemEventMessageProps> = ({
  message,
  index,
  isSelected = false,
  onClick,
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const event = parseSystemEvent(message)

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick?.(index)
    setIsExpanded((expanded) => !expanded)
  }

  return (
    <div data-message-index={index} className={`relative ${className}`}>
      <div
        className={`rounded-lg border border-coval-border bg-surface-0 text-coval-text transition-all duration-150 ${
          isSelected ? "ring-2 ring-coval-contrast" : ""
        }`}
      >
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-surface-1 focus:outline-none"
          aria-label={`Toggle websocket event details for ${
            event.eventName ?? event.eventType
          }`}
          aria-expanded={isExpanded}
          onClick={handleToggle}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Radio className="h-3.5 w-3.5 shrink-0 text-coval-text-muted" />
            <div className="min-w-0">
              <div className="truncate text-xs font-medium">
                websocket_event
              </div>
              <div className="truncate text-[11px] text-coval-text-muted">
                {event.eventName
                  ? `${event.eventType} / ${event.eventName}`
                  : event.eventType}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 text-coval-text-muted">
            <TimestampDisplay
              message={message}
              className="text-right text-xs"
            />
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="border-t border-coval-border px-3 py-2">
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-coval-text">
              {event.detailsText}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
